import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { createSharedComposable } from '@vueuse/core'
import { computed, ref } from 'vue'

import { ReApi, ReApiVersion, ReEndpoint } from '@/composables/query'
import utils from '@/plugins/utils'
import store, { Actions } from '@/store'
import getUserLabel from '@/utils/getUserLabel'

interface UserRoleMap {
  [module: string]: string[]
}

interface BillingInfo {
  address_city?: string
  address_country?: string
  address_line1?: string
  address_line2?: string
  address_postal_code?: string
  address_state?: string
  name?: string
  vat_number?: string
}

interface User {
  id?: string
  username?: string
  preferred_lang?: string
  roles?: UserRoleMap
  authenticated?: boolean
  billing_type?: string
  billing?: BillingInfo
  expert_zones?: string[]
  [key: string]: unknown
}

export const useCurUser = createSharedComposable(() => {
  const query = ref()
  const queryKey = [ReApi.AUTH, ReApiVersion.CURRENT, ReEndpoint.USER]
  const queryClient = useQueryClient()

  query.value = useQuery<User>({
    queryKey,
    enabled: () => store.getters.isAuthenticated,
    staleTime: Infinity,
    placeholderData: {},
  })

  // Access the shared core state.
  const data = computed<User>(() => query.value?.data)
  const roles = computed<UserRoleMap>(() => data.value?.roles || {})
  const retail = computed(() => query.value.data?.category === 'retail')

  const hasRole = (role: string, module?: string, only?: boolean): boolean => {
    if (!data.value?.roles) return false
    if (!utils.containsRole(roles.value, role, module)) return false
    if (only) {
      const flatRoles = Object.values(roles.value).flat()
      if (module) return flatRoles.length === 1
      else return flatRoles.filter((r) => r !== role).length === 0
    }
    return true
  }

  const modulesForRole = (role: string): string[] => {
    return Object.entries(roles.value)
      .filter(([k, v]) => v.length && !['-', '*'].includes(k))
      .filter(([_, v]) => v.includes(role))
      .map(([k, _]) => k)
  }

  const is = computed(() => ({
    admin: hasRole('admin', undefined, false),
    valuer: hasRole('valuer', undefined, false),
    borrower: hasRole('borrower', undefined, false),
    catSubmitter: hasRole('submitter', 'cat', false),
    catExplorer: hasRole('explorer', 'cat', false),
    dvmSubmitter: hasRole('submitter', 'dvm', false),
    dvmDispatcher: hasRole('dispatcher', 'dvm', false),
    dvmValuer: hasRole('valuer', 'dvm', false),
    ovmValuer: hasRole('valuer', 'ovm', false),
    ovmSubmitter: hasRole('submitter', 'ovm', false),
    ovmBorrower: hasRole('borrower', 'ovm', false),
    oepcValuer: hasRole('valuer', 'oepc', false),
    oepcDispatcher: hasRole('dispatcher', 'oepc', false),
    onlyValuer: hasRole('valuer', undefined, true),
    ovmDispatcher: hasRole('dispatcher', 'ovm', false),
    onsiteDispatcher: hasRole('dispatcher', 'ovm', false) || hasRole('dispatcher', 'oepc', false),
    onlyBorrower: hasRole('borrower', undefined, true),
    support: hasRole('support'),
    hotliner: hasRole('hotliner'),
    retail: retail.value,
  }))

  const submitableModules = computed(() => [...modulesForRole('submitter'), ...modulesForRole('explorer')])
  const submitableValuations = computed(() =>
    submitableModules.value.filter((m) => ['avm', 'dvm', 'ovm'].includes(m))
  )

  const authenticated = computed(
    () => (data.value?.authenticated ?? store.getters.isAuthenticated) as boolean
  )

  const accessibleModules = computed(() => {
    const modules = Object.entries(roles.value)
      .filter(([k, v]) => v.length && !['-', '*'].includes(k))
      .map(([k, _]) => k)
    if (!modules.includes('ovm') && is.value.dvmSubmitter) {
      modules.push('ovm')
    }
    return modules
  })

  const availableApps = computed(() => {
    const apps: string[] = []
    if (submitableValuations.value.length > 0) apps.push('valuation')
    if (submitableModules.value.includes('ers')) apps.push('ers')
    if (submitableModules.value.includes('oepc')) apps.push('oepc')
    if (submitableModules.value.includes('cat')) apps.push('cat')
    return apps
  })

  const { mutateAsync } = useMutation({
    onMutate: async (variables: { data: Partial<User> }) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<User>(queryKey)
      if (previous) {
        const optimistic_next = { ...previous, ...variables.data }
        queryClient.setQueryData(queryKey, optimistic_next)
        return { previous }
      }
      return {}
    },
    onSuccess: (result: User) => {
      queryClient.setQueryData(queryKey, result)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (_error: any, _variables: any, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },
  })

  // @ts-expect-error verb is not defined on type
  const patch = (data: Partial<User>) => mutateAsync({ verb: 'PATCH', queryKey, data })

  function logout() {
    store.dispatch(Actions.LOGOUT)
  }

  return {
    isLoading: computed(() => query.value.isLoading),
    isFetching: computed(() => query.value.isFetching),
    isError: computed(() => query.value.isError),
    error: computed(() => query.value.error),
    data: computed(() => query.value.data),
    patch,
    roles,
    hasRole,
    is,
    modulesForRole,
    userId: computed(() => data.value?.id),
    username: computed(() => data.value?.username),
    email: computed(() => data.value?.email),
    organization: computed(() => data.value?.organization),
    userLabel: computed(() => getUserLabel(data.value)),
    authenticated,
    accessibleModules,
    submitableModules,
    submitableValuations,
    availableApps,
    logout,
  }
})
